import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowLeft, ArrowRight, FileText } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import ihelmWhitepaperCover from "@/assets/ihelm-whitepaper-cover.png.asset.json";
import energyWhitepaperFull from "@/assets/energy-whitepaper-full.png.asset.json";
import timetableWhitepaperCover from "@/assets/timetable-whitepaper-full.png.asset.json";
import electrificationWhitepaperFull from "@/assets/electrification-whitepaper-full.png.asset.json";

const WhitePapers = () => {
  const { t, i18n } = useTranslation('blog');

  const staticWhitePapers = [
    {
      id: "static-timetable-wp",
      slug: "timetable-optimization-white-paper",
      title: "From schedules to efficiency: The hidden potential in your vessel timetable",
      excerpt: "How operational data, vessel-specific modelling and simulation help you find a smarter timetable before changing real operations.",
      featured_image: timetableWhitepaperCover.url,
      downloadUrl: "/timetable-optimization-white-paper",
    },
    {
      id: "static-electrification-wp",
      slug: "electrification-white-paper",
      title: "Is your fleet ready for electrification? The future of maritime operations",
      excerpt: "How operational data, digital twins and AI help you choose the right electrification path, vessel by vessel and across the fleet.",
      featured_image: electrificationWhitepaperFull.url,
      downloadUrl: "/electrification-white-paper",
    },
    {
      id: "static-energy-wp",
      slug: "energy-optimization-white-paper",
      title: "From data to efficiency: The fastest path to energy optimization in maritime operations",
      excerpt: "Learn how decision support, digital twins and AI turn vessel data into measurable efficiency gains across your fleet.",
      featured_image: energyWhitepaperFull.url,
      downloadUrl: "/energy-optimization-white-paper",
    },
    {
      id: "static-ihelm-wp",
      slug: "ihelm-white-paper",
      title: "From data to decisions: Why vessel data alone isn't enough to improve maritime operations",
      excerpt: "White paper about how iHelm turns vessel data into actionable decisions that improve maritime operations.",
      featured_image: ihelmWhitepaperCover.url,
      downloadUrl: "/ihelm-white-paper",
    },
    {
      id: "static-cetafuel-wp",
      slug: "cetafuel-white-paper",
      title: "How to measure fuel consumption on vessels - without physical flow meters",
      excerpt: "From estimates to insights white paper about our virtual fuel flow meter CetaFuel",
      featured_image: "/images/cetafuel-whitepaper-cover.png",
      downloadUrl: "/whitepaper/cetafuel-download",
    },
  ];


  const { data: posts, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["whitepapers-v2"],
    staleTime: 10 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_categories")
        .select("slug, blog_post_categories(blog_posts(id, title, slug, excerpt, content, featured_image, published_at, pdf_url, status))")
        .eq("slug", "whitepaper")
        .maybeSingle();
      if (error) throw error;
      const list = ((data?.blog_post_categories || []) as any[])
        .map((bpc: any) => bpc.blog_posts)
        .filter((p: any) => p && p.status === "published" && (!p.published_at || new Date(p.published_at) <= new Date()))
        .sort((a: any, b: any) => (b.published_at || "").localeCompare(a.published_at || ""));
      return list;
    },
  });

  const getExcerpt = (post: any) => {
    if (post.excerpt) return post.excerpt;
    if (post.content) { const stripped = post.content.replace(/<[^>]*>/g, ""); return stripped.substring(0, 200) + (stripped.length > 200 ? "..." : ""); }
    return "";
  };

  const LOCALE_MAP: Record<string, string> = { en: 'en-US', sv: 'sv-SE', ja: 'ja-JP', zh: 'zh-CN' };
  const formatDate = (date: string) => new Date(date).toLocaleDateString(LOCALE_MAP[i18n.language] || 'en-US', { year: "numeric", month: "long", day: "numeric" });
  const visibleStaticWhitePapers = staticWhitePapers.filter(
    (sp) => !(posts || []).some((post: any) => post.slug === sp.slug || post.title === sp.title),
  );


  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('whitepapers.meta.title')}</title>
        <meta name="description" content={t('whitepapers.meta.description')} />
        <link rel="canonical" href="https://cetasol.com/whitepapers" />
        <meta property="og:title" content={t('whitepapers.meta.title')} />
        <meta property="og:description" content={t('whitepapers.meta.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cetasol.com/whitepapers" />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('whitepapers.meta.title')} />
        <meta name="twitter:description" content={t('whitepapers.meta.description')} />
        <meta name="twitter:image" content="https://cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "Insight", "item": "https://cetasol.com/news" },
            { "@type": "ListItem", "position": 3, "name": "White Papers", "item": "https://cetasol.com/whitepapers" }
          ]
        })}</script>
      </Helmet>
      <Header />
      <main className="min-h-screen pt-24 pb-16 section-fade">
        <div className="container mx-auto px-6">
          <Link to="/news" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
            <ArrowLeft size={16} /> {t('category.backToInsight')}
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('whitepapers.heading')}</h1>
          <p className="text-muted-foreground text-lg mb-10">{t('whitepapers.description')}</p>

          {isError && (
            <div className="py-8 mb-8 text-center border border-border">
              <p className="text-muted-foreground mb-4">{i18n.language === 'sv' ? 'Kunde inte ladda alla white papers. Försök igen om en stund.' : 'Could not load all white papers. Please try again in a moment.'}</p>
              <button onClick={() => refetch()} disabled={isFetching} className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-primary border border-primary hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50">
                {isFetching ? (i18n.language === 'sv' ? 'Laddar…' : 'Loading…') : (i18n.language === 'sv' ? 'Försök igen' : 'Try again')}
              </button>
            </div>
          )}
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-card mb-4" />
                  <div className="h-4 bg-card rounded w-1/3 mb-2" />
                  <div className="h-3 bg-card rounded w-full mb-1" />
                  <div className="h-3 bg-card rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
              {visibleStaticWhitePapers.map((sp, idx) => (
                <motion.div key={sp.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: idx * 0.1 }}>
                  <Link to={sp.downloadUrl} className="group flex flex-col h-full border border-border p-6 hover:border-primary/40 transition-colors">
                    <div className="relative aspect-[4/3] overflow-hidden mb-4">
                      <img src={sp.featured_image} alt={sp.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    </div>
                    <h2 className="font-display font-bold text-foreground text-lg tracking-wide mb-2 line-clamp-2">{sp.title}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-2 flex-1 line-clamp-3">{sp.excerpt}</p>
                    <span className="text-sm font-medium text-primary group-hover:underline inline-flex items-center gap-1 mt-auto">Download <ArrowRight size={14} /></span>
                  </Link>
                </motion.div>
              ))}

              {/* Dynamic white papers from DB */}
              {posts?.map((post: any, idx: number) => (
                <motion.div key={post.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (idx + visibleStaticWhitePapers.length) * 0.1 }}>
                  <Link to={`/${post.slug}`} className="group flex flex-col h-full border border-border p-6 hover:border-primary/40 transition-colors">
                    <div className="relative aspect-[4/3] overflow-hidden mb-4">
                      {post.featured_image ? (<img src={post.featured_image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />) : (<div className="w-full h-full bg-card flex items-center justify-center"><FileText size={48} className="text-primary/30" /></div>)}
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">{formatDate(post.published_at || "")}</p>
                    <h2 className="font-display font-bold text-foreground text-lg tracking-wide mb-2 line-clamp-2">{post.title}</h2>

                    <p className="text-sm text-muted-foreground leading-relaxed mb-2 flex-1 line-clamp-3">{getExcerpt(post)}</p>
                    <span className="text-sm font-medium text-primary group-hover:underline inline-flex items-center gap-1 mt-auto">{t('whitepapers.readMore')} <ArrowRight size={14} /></span>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

        </div>
      </main>
      <Footer />
    </>
  );
};

export default WhitePapers;
